// What a wall button IS — the three kinds, and the messages each sends.
//
// The difference between them is whether the button holds STATE:
//
//   toggle     a switch channel. Lit when the load is on; tapping sends the
//              opposite. One button per load rather than two.
//   momentary  fires and forgets — curtain Open/Close/Stop, and scenes. There
//              is nothing to light up, because "open" is not a state a curtain
//              reports back.
//   navigate   jumps to another panel (unchanged).
//
// ── Where the kind is stored ───────────────────────────────────────────────
// WallButton.ButtonType. It held only 'action' and 'navigate' in live data, so
// 'toggle' is a new value — VERIFIED against the server before building on it
// (written, read back intact, and the probe row deleted). The column is not
// validated, so a third kind needs no backend change.
//
// A toggle stores the ON message ("3-1") as its single action step. That is
// enough to recover both the channel and the off message, so no extra column is
// needed to describe it.

import { publishCommand } from '../utils/deviceController';
import { parseStatus } from '../devices/statusFormat';
import { FAN_SPEEDS, fanCommand, fanStepLabel, parseFanSpeed } from '../devices/fan';
import { FAN_LADDER, fanStepLabelOf } from '../devices/statusFormat';
import { RGB_PRESETS, WWH_PRESETS, WWRGB_PRESETS } from '../devices/light';
import { composeLabel } from './wallLabel';

export const KIND_TOGGLE = 'toggle';
export const KIND_MOMENTARY = 'action';
export const KIND_NAVIGATE = 'navigate';

// A fan is not one button. The hardware takes six discrete duties and a wall
// panel offering only on/off would throw four of them away — so a fan slot
// draws the whole ladder inside its single cell.
export const KIND_FAN = 'fan';

// [{ speed, label, message }] — Off, 1, 2, 3, 4, Max. Labels come from the fan
// screen's own fanStepLabel, so the wall and the dial say the same words.
// WARNING: THE CHANNEL IS NOT ALWAYS 1. A standalone fan (type 53) is
// single-channel, which is why core/devices/fan hardcodes "1-<speed>". On a 7+1
// the fan is channel 8, on a 7+2 they are 8 and 9 — sending "1-60" there sets
// the speed of a LIGHT, which the firmware accepts and nobody would trace back
// here. So the channel travels with the button.
// ⚠️ TWO LADDERS, AND THE CHANNEL IS WHAT CHOOSES BETWEEN THEM.
//
//   channel 1   a STANDALONE fan (type 53 and friends), Flutter-era hardware
//               over MQTT: six steps, {10,20,40,60,80,100}, from devices/fan.
//   channel > 1 a fan inside a PANEL — 4+1 channel 5, 7+1 channel 8, 7+2
//               channels 8 and 9. Off plus eight running steps,
//               {10,30,40,50,60,70,80,90,100}, from devices/statusFormat —
//               which is the HARDWARE's ladder, not the stale FAN_MAP in the
//               firmware source. Read the note there before changing it.
//
// Confirmed against the hardware. Using the six-step ladder on a panel fan
// leaves 30, 50, 70 and 90 unreachable, and a fan already running at one of them
// matches no button — so the cluster shows nothing selected while the fan is
// plainly turning.
export const fanStepsFor = (channel = 1) => {
  const standalone = Number(channel) === 1;
  const ladder = standalone ? FAN_SPEEDS : FAN_LADDER;
  const labelOf = standalone ? fanStepLabel : fanStepLabelOf;
  return ladder.map((speed) => ({
    speed,
    label: labelOf(speed),
    message: standalone ? fanCommand(speed) : `${channel}-${speed}`,
  }));
};

// The single-channel set, for a standalone fan.
export const FAN_STEPS = fanStepsFor(1);

/**
 * The caption suggested while a slot is being edited: "Room · Device · Button".
 *
 * The room is included here because the wizard cannot yet know how many rooms
 * the finished sheet will cover. Whether it SURVIVES is decided for the panel
 * as a whole once the button is saved — see core/wall/wallLabel, which owns the
 * rule and is the single implementation.
 */
export const defaultButtonLabel = (parts) => composeLabel(parts, { showRoom: true });

/** The device a curtain/gate slot drives, or null. */
export function curtainTarget(button) {
  if (button?.ButtonType !== KIND_CURTAIN) return null;
  const uno = (button.Actions || [])[0]?.UniqueNumber;
  return uno ? { uniqueNumber: String(uno) } : null;
}

/** The device a lock slot drives, or null. */
export function lockTarget(button) {
  if (button?.ButtonType !== KIND_LOCK) return null;
  const step = (button.Actions || [])[0];
  return step?.UniqueNumber
    ? { uniqueNumber: String(step.UniqueNumber), message: step.MqttMessage || LOCK_OPEN }
    : null;
}

/** The device AND CHANNEL a fan slot drives, or null. */
export function fanTarget(button) {
  if (button?.ButtonType !== KIND_FAN) return null;
  const step = (button.Actions || [])[0];
  if (!step || !step.UniqueNumber) return null;
  const m = /^(\d+)-(\d+)$/.exec(String(step.MqttMessage || ''));
  return { uniqueNumber: String(step.UniqueNumber), channel: m ? Number(m[1]) : 1 };
}

/**
 * The fan's current speed from its status, or null when nothing has arrived.
 *
 * WARNING: 10 IS OFF, NOT 0 — see core/devices/fan. Treating 0 as the off step
 * would leave the Off button never looking selected.
 */
export function fanSpeedOf(button, statusText) {
  const target = fanTarget(button);
  if (!target || !statusText) return null;
  // On a bank the fan is one value among many, so read ITS position. A
  // standalone fan reports "1-60", which parseStatus renders as a single value.
  const parsed = parseStatus(statusText);
  if (parsed && parsed.values.length > 1) {
    const v = parsed.values[target.channel - 1];
    return v == null ? null : Number(v);
  }
  return parseFanSpeed(statusText);
}

// ⚠️ EXACTLY the literals CurtainControlCard publishes. A second vocabulary for
// the same three actions is how two paths drift until one of them stops working.
export const CURTAIN_OPEN = 'OPENCURTAIN';
export const CURTAIN_STOP = 'STOPCURTAIN';
export const CURTAIN_CLOSE = 'CLOSECURTAIN';

// Curtain and sliding gate: three actions that belong TOGETHER. Open without
// Stop is a gate you cannot halt half-way, so they share one slot rather than
// competing for three.
export const KIND_CURTAIN = 'curtain';

// A door lock fires only after the account password. It is the one button on a
// wall panel that must not be pressable in passing — the panel is by the door,
// and anyone standing in front of it can reach it.
export const KIND_LOCK = 'lock';

// A colour light is its presets. One on/off button would throw away every
// colour the product exists for, so the slot holds the same swatches the light
// screen offers.
//
// ⚠️ THE TYPE IDS ARE NOT IN THE ORDER ANYONE GUESSES: 24 is rgb, 25 is wwh and
// 23 is wwrgb (deviceMeta.deviceTypeKey). Mapping them by eye puts a 3-channel
// preset on a 5-channel light, which the firmware accepts and silently gets
// wrong.
export const KIND_LIGHT = 'light';

const LIGHT_PRESETS = { rgb: RGB_PRESETS, wwh: WWH_PRESETS, wwrgb: WWRGB_PRESETS };

export const lightPresetsFor = (typeKey) => LIGHT_PRESETS[typeKey] || [];

export const isLightKey = (typeKey) => Boolean(LIGHT_PRESETS[typeKey]);

/** The device a light slot drives, plus which preset set it uses. */
export function lightTarget(button) {
  if (button?.ButtonType !== KIND_LIGHT) return null;
  const step = (button.Actions || [])[0];
  if (!step?.UniqueNumber) return null;
  // The preset set is recovered from the stored command's WIDTH — 3 values is
  // rgb, 2 is wwh, 5 is wwrgb — so no extra column records it.
  const width = String(step.MqttMessage || '').split(',').length;
  // eslint-disable-next-line no-nested-ternary
  const typeKey = width === 5 ? 'wwrgb' : width === 2 ? 'wwh' : 'rgb';
  return { uniqueNumber: String(step.UniqueNumber), typeKey };
}
/*
 * A current sensor — DeviceID 82 (single phase) and 16 (three phase).
 *
 * ⚠️ IT HAS NOTHING TO PRESS, and that is why it needs its own kind. A meter has
 * no relay and no channel: given the default toggle treatment it drew a tile
 * that said "—" forever, and tapping it published `1-1` at a device with no
 * output to switch. It is a READOUT — the slot shows what the load is doing.
 *
 * The readings do not arrive over MQTT the way a switch's state does; they are
 * rows in CurrentSense, ~30 s apart, read through core/devices/phase.
 */
export const KIND_METER = 'meter';

/** The device a meter slot reads. */
export function meterTarget(button) {
  if (button?.ButtonType !== KIND_METER) return null;
  const step = (button.Actions || [])[0];
  if (!step?.UniqueNumber) return null;
  return { uniqueNumber: String(step.UniqueNumber) };
}

export const LOCK_OPEN = 'OPENDOOR';

export const CURTAIN_ACTIONS = [
  { label: 'Open', message: CURTAIN_OPEN },
  { label: 'Stop', message: CURTAIN_STOP },
  { label: 'Close', message: CURTAIN_CLOSE },
];

/** "3-1" -> 3. null when the message is not a channel command. */
export function channelOf(message) {
  const m = /^(\d+)-[01]$/.exec(String(message || '').trim());
  return m ? Number(m[1]) : null;
}

export const onMessage = (channel) => `${channel}-1`;
export const offMessage = (channel) => `${channel}-0`;

/** The device + channel a toggle button drives, or null. */
export function toggleTarget(button) {
  if (button?.ButtonType !== KIND_TOGGLE) return null;
  const step = (button.Actions || [])[0];
  if (!step) return null;
  const channel = channelOf(step.MqttMessage);
  if (channel == null || !step.UniqueNumber) return null;
  return { uniqueNumber: String(step.UniqueNumber), channel };
}

/**
 * Is this toggle's load on, given the device's latest status string?
 *
 * -> true / false / null when nothing has been heard yet.
 *
 * ⚠️ NULL IS NOT FALSE. A wall panel that shows every button as "off" until the
 * first status arrives invites someone to press one that is already on. Unknown
 * has to look different from off.
 */
export function isToggleOn(button, statusText) {
  const target = toggleTarget(button);
  if (!target || !statusText) return null;
  const parsed = parseStatus(statusText);
  if (!parsed) return null;
  const v = parsed.values[target.channel - 1];
  if (v == null) return null;
  return Number(v) === 1;
}

/**
 * Press a button.
 *
 * A toggle sends the OPPOSITE of what it currently shows; unknown is treated as
 * off, so the first press turns the load on — the safer guess for someone
 * standing at a dark switch.
 */
export function pressButton(button, { userId, isOn }) {
  const target = toggleTarget(button);
  if (target) {
    const next = isOn === true ? offMessage(target.channel) : onMessage(target.channel);
    publishCommand(target.uniqueNumber, next, userId);
    return next;
  }
  return null;   // momentary buttons run their sequence through wallApi
}
